import { useCallback, useEffect, useRef, useState } from 'react';
import { ServiceMediaUploads } from 'services/media-uploads';
import * as yup from 'yup';
import { useMountedRef } from './use-mounted-ref';

const schema = yup.string().url();
interface Props {
  value: string | undefined | null;
  name?: string;
  innerValue?: string;
  isGridTemplate?: boolean;
}
export const useLoadHtml = ({ value, name, innerValue, isGridTemplate }: Props) => {
  const [html, setHtml] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [loadedFromUrl, setLoadedFromUrl] = useState(false);
  const lastLoadedUrlRef = useRef<string | null>(null);

  const mountedRef = useMountedRef();
  const onLoad = useCallback(async (url: string) => {
    setIsLoading(true);
    setLoadedFromUrl(false);
    try {
      const response = await ServiceMediaUploads.getHtml(url);

      if (!mountedRef.current) return;

      if (response?.data) {
        setHtml(response.data);
        setLoadedFromUrl(true);
        lastLoadedUrlRef.current = url;
      }
    } finally {
      if (mountedRef.current) setIsLoading(false);
    }
  }, [mountedRef, setHtml, setIsLoading]);

  const onSaveToCloud = useCallback(async (value: string) => {
    if (name) {
      const {
        data: { filePath }
      } = await ServiceMediaUploads.uploadHtml({ value, name });
      onLoad(filePath);
    }
  }, [name, onLoad]);

  useEffect(() => {
    if (isGridTemplate && typeof value === 'string' && !schema.isValidSync(value)) {
      setHtml(value);
      return;
    }

    if (!value) return;

    const isUrl = schema.isValidSync(value);
    const isEditorStillOnInitialValue = innerValue === undefined || innerValue === value;

    // Load when the stored value is a URL, even if the editor has already
    // echoed that URL back into `innerValue` during init.
    if (
      isUrl &&
      isEditorStillOnInitialValue &&
      !isLoading &&
      lastLoadedUrlRef.current !== value
    ) {
      setLoadedFromUrl(false);
      onLoad(value);
      return;
    }

    // When the stored value is raw HTML (not a URL), the legacy behavior is to
    // upload it once (initial mount) and then load it back from the returned URL.
    if (!isUrl && innerValue === undefined) {
      onSaveToCloud(value);
    }
  }, [value, onLoad, onSaveToCloud, isGridTemplate, innerValue, isLoading]);

  return { html, isLoading, loadedFromUrl };
};
